import { redirect } from 'react-router-dom'
import { getSubscriptionStatus } from '../helpers/revenueCat'
import { getUserCurrency } from '../helpers/sessionStorage'
import { getSession, listStripePrices } from '../helpers/supabase'
import { StripePrice } from '../types'

export interface GetPricesData {
  currency: string
  subscriptionStatus: 'active' | 'inactive' | 'none'
  topPrice: StripePrice | undefined
  topIntroPrice: StripePrice | undefined
  firstPrice: StripePrice | undefined
  firstIntroPrice: StripePrice | undefined
  secondPrice: StripePrice | undefined
  secondIntroPrice: StripePrice | undefined
  oneTimePrice: StripePrice | undefined
}

export default async function getPricesLoader(options?: {
  pricingVariant?: string
}): Promise<GetPricesData | Response> {
  const [{ data }, stripePrices] = await Promise.all([
    getSession(),
    listStripePrices(),
  ])

  const currency = await findCurrency({ stripePrices })

  const userId = data?.session?.user?.id

  if (!userId) {
    return redirect('/sign-up')
  }

  const subscriptionStatus = await getSubscriptionStatus(userId)

  const lookupKeys = getLookupKeys(options?.pricingVariant)

  return {
    currency: currency,
    subscriptionStatus: subscriptionStatus,
    topPrice: lookupKeys.topPrice
      ? findStripePrice({
          stripePrices,
          lookupKey: lookupKeys.topPrice,
        })
      : undefined,
    topIntroPrice: lookupKeys.topIntroPrice
      ? findStripePrice({
          stripePrices,
          lookupKey: lookupKeys.topIntroPrice,
        })
      : undefined,
    firstPrice: findStripePrice({
      stripePrices,
      lookupKey: lookupKeys.firstPrice,
    }),
    firstIntroPrice: findStripePrice({
      stripePrices,
      lookupKey: lookupKeys.firstIntroPrice,
    }),
    secondPrice: lookupKeys.secondPrice
      ? findStripePrice({
          stripePrices,
          lookupKey: lookupKeys.secondPrice,
        })
      : undefined,
    secondIntroPrice: lookupKeys.secondIntroPrice
      ? findStripePrice({
          stripePrices,
          lookupKey: lookupKeys.secondIntroPrice,
        })
      : undefined,
    oneTimePrice: lookupKeys.oneTimePrice
      ? findStripePrice({
          stripePrices,
          lookupKey: lookupKeys.oneTimePrice,
        })
      : undefined,
  }
}

interface LookupKeys {
  topPrice?: string
  topIntroPrice?: string
  firstPrice: string
  firstIntroPrice: string
  secondPrice?: string
  secondIntroPrice?: string
  oneTimePrice?: string
}

function getLookupKeys(pricingVariant?: string): LookupKeys {
  const appId = import.meta.env.VITE_SUPABASE_APP_ID as string

  // Three-plan "-vo" price set: monthly, 3-monthly (recommended top) and
  // yearly, each with its own intro → full renewal price. Shared by the Lingo
  // "vo" funnel and by Bumi's main pricing.
  const voLookupKeys: LookupKeys = {
    topPrice: 'm3-full-vo',
    topIntroPrice: 'm3-intro-vo',
    firstPrice: 'm-full-vo',
    firstIntroPrice: 'm-intro-vo',
    secondPrice: 'y-full-vo',
    secondIntroPrice: 'y-intro-vo',
  }

  if (pricingVariant === 'vo') {
    return voLookupKeys
  }

  // Lingo trial funnel: the yearly plan sold at its full price (y-full-2,
  // 119.99) behind a 7-day trial, so it keeps its own key set now that the main
  // funnel leads with the discounted y-intro-v3 intro.
  if (pricingVariant === 'trial') {
    return {
      firstPrice: 'y-full-2',
      firstIntroPrice: 'y-intro-2',
    }
  }

  // Weekly first, yearly second (existing apps)
  const weeklyFirstApps = ['run-fusion', 'unvape', 'glow-minder', 'morphcasa']
  if (weeklyFirstApps.includes(appId)) {
    return {
      firstPrice: 'w-full',
      firstIntroPrice: 'w-intro',
      secondPrice: 'y-full',
      secondIntroPrice: 'y-intro',
    }
  }

  // Yearly first, weekly second (existing apps)
  const yearlyFirstWeeklySecondApps = [
    'date-wing-ai',
    'decluttify',
    'feel-shift',
    'fridge-guru',
    'nite-whisper',
    'pillowpath',
    'receipt-guardian',
    'sober-spark',
  ]
  if (yearlyFirstWeeklySecondApps.includes(appId)) {
    return {
      firstPrice: 'y-full',
      firstIntroPrice: 'y-intro',
      secondPrice: 'w-full',
      secondIntroPrice: 'w-intro',
    }
  }

  // Monthly first, yearly second
  const monthlyFirstApps = ['longevity-compass']
  if (monthlyFirstApps.includes(appId)) {
    return {
      firstPrice: 'm-full',
      firstIntroPrice: 'm-intro',
      secondPrice: 'y-full',
      secondIntroPrice: 'y-intro',
    }
  }

  // Lingo: a single yearly plan (29.99 intro renewing at 119.99) as the
  // highlighted top offer, with the monthly plan (9.99 intro → 19.99) beneath
  // it. No second yearly box.
  if (appId === 'lingo-chat-ai') {
    return {
      topPrice: 'y-full-2',
      topIntroPrice: 'y-intro-v3',
      firstPrice: 'm-full',
      firstIntroPrice: 'm-intro',
    }
  }

  // Bumi: three-plan "-vo" price set (monthly / 3-monthly / yearly).
  if (appId === 'bumi') {
    return voLookupKeys
  }

  const yearlyFirstMonthlySecondApps = ['acri', 'terra-scan']
  if (yearlyFirstMonthlySecondApps.includes(appId)) {
    return {
      firstPrice: 'y-full',
      firstIntroPrice: 'y-intro',
      secondPrice: 'm-full',
      secondIntroPrice: 'm-intro',
    }
  }

  // Default for new apps: monthly first, yearly second
  return {
    firstPrice: 'm-full',
    firstIntroPrice: 'm-intro',
    secondPrice: 'y-full',
    secondIntroPrice: 'y-intro',
  }
}

async function findCurrency({
  stripePrices,
}: {
  stripePrices: StripePrice[]
}): Promise<string> {
  const userCurrency = (await getUserCurrency()).toLowerCase()

  const stripePriceCurrencies = stripePrices.flatMap((price) => [
    price.currency,
    ...Object.keys(price?.currency_options),
  ])

  return stripePriceCurrencies.includes(userCurrency) ? userCurrency : 'eur'
}

function findStripePrice({
  lookupKey,
  stripePrices,
}: {
  lookupKey: string
  stripePrices: StripePrice[]
}): StripePrice | undefined {
  return stripePrices.find((price) => price.lookup_key === lookupKey)
}
